import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  ArrowLeft,
  Reply,
  Download,
  User,
  Calendar,
  Clock,
  CheckCircle,
  AlertCircle,
  FileText,
  Mail,
  Send,
} from "lucide-react";
import { useMessage, useReplyToMessage } from "@/hooks/use-messagerie";
import { useAuth } from "@/hooks/use-auth";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { cn } from "@/lib/utils";

const MessageDetail = ({ messageId, onBack }) => {
  const [replyText, setReplyText] = useState("");
  const [showReplyForm, setShowReplyForm] = useState(false);

  const { user } = useAuth();
  const { data: messageData, isLoading, error } = useMessage(messageId);
  const replyMutation = useReplyToMessage();

  const message = messageData?.data;

  // Fonctions utilitaires
  const getPriorityColor = (priority) => {
    switch (priority) {
      case "urgente":
        return "destructive";
      case "importante":
        return "secondary";
      case "normale":
      default:
        return "outline";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "traite":
        return "default";
      case "en_attente":
        return "secondary";
      case "envoye":
      default:
        return "outline";
    }
  };

  const canReply = () => {
    return user && ["admin", "rh", "manager"].includes(user.role);
  };

  const isRecipient = () => {
    return user && message && user.id === message.destinataire_id;
  };

  const isSender = () => {
    return user && message && user.id === message.employe_id;
  };

  // Gestion de la réponse
  const handleReply = () => {
    if (!replyText.trim()) {
      return;
    }

    replyMutation.mutate(
      { messageId: message.id, reponse: replyText },
      {
        onSuccess: () => {
          setReplyText("");
          setShowReplyForm(false);
        },
      }
    );
  };

  // Téléchargement du fichier joint (base64)
  const handleDownloadFile = () => {
    if (message.has_attachment && message.file_data) {
      try {
        // Convertir le base64 en blob
        const byteCharacters = atob(message.file_data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: message.file_type });

        // Créer un lien de téléchargement
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = message.file_name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Erreur lors du téléchargement:", error);
      }
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto p-6 max-w-4xl">
        <div className="space-y-6">
          {/* Skeleton */}
          <div className="flex items-center gap-4">
            <div className="h-10 w-20 bg-muted rounded animate-pulse"></div>
            <div className="h-8 w-48 bg-muted rounded animate-pulse"></div>
          </div>
          <div className="h-96 bg-muted rounded animate-pulse"></div>
        </div>
      </div>
    );
  }

  if (error || !message) {
    return (
      <div className="container mx-auto p-6 max-w-4xl">
        <div className="text-center py-12">
          <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">Message non trouvé</h2>
          <p className="text-muted-foreground mb-4">
            {error?.response?.data?.message ||
              "Ce message n'existe pas ou vous n'avez pas les permissions pour le consulter."}
          </p>
          <Button onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      <div className="space-y-6">
        {/* En-tête */}
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold line-clamp-1">{message.objet}</h1>
            <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
              <div className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                {format(new Date(message.date_envoi), "dd MMMM yyyy à HH:mm", {
                  locale: fr,
                })}
              </div>
              {message.date_lecture && (
                <div className="flex items-center gap-1">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  Lu le{" "}
                  {format(new Date(message.date_lecture), "dd/MM à HH:mm", {
                    locale: fr,
                  })}
                </div>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            <Badge variant={getPriorityColor(message.priorite)}>
              {message.priorite}
            </Badge>
            <Badge variant={getStatusColor(message.statut)}>
              {message.statut?.replace("_", " ")}
            </Badge>
          </div>
        </div>

        {/* Informations principales */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Détails du message
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Participants */}
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-muted-foreground">
                  Expéditeur
                </Label>
                <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                  <User className="h-8 w-8 p-1 bg-primary text-primary-foreground rounded-full" />
                  <div>
                    <div className="font-medium">
                      {message.employe?.prenom} {message.employe?.nom}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {message.employe?.email}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {message.employe?.role}
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium text-muted-foreground">
                  Destinataire
                </Label>
                <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                  <User className="h-8 w-8 p-1 bg-secondary text-secondary-foreground rounded-full" />
                  <div>
                    <div className="font-medium">
                      {message.destinataire?.prenom} {message.destinataire?.nom}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {message.destinataire?.email}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {message.destinataire?.role}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Métadonnées */}
            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-1">
                <Label className="text-sm font-medium text-muted-foreground">
                  Type
                </Label>
                <p className="text-sm">{message.type_demande || "Message"}</p>
              </div>
              <div className="space-y-1">
                <Label className="text-sm font-medium text-muted-foreground">
                  Priorité
                </Label>
                <Badge
                  variant={getPriorityColor(message.priorite)}
                  className="w-fit"
                >
                  {message.priorite}
                </Badge>
              </div>
              <div className="space-y-1">
                <Label className="text-sm font-medium text-muted-foreground">
                  Statut
                </Label>
                <Badge
                  variant={getStatusColor(message.statut)}
                  className="w-fit"
                >
                  {message.statut?.replace("_", " ")}
                </Badge>
              </div>
            </div>

            {/* Description */}
            {message.description && (
              <div className="space-y-2">
                <Label className="text-sm font-medium text-muted-foreground">
                  Description
                </Label>
                <p className="text-sm p-3 bg-muted/50 rounded-lg">
                  {message.description}
                </p>
              </div>
            )}

            {/* Motif et justification */}
            {(message.motif || message.justification) && (
              <div className="grid gap-4 md:grid-cols-2">
                {message.motif && (
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-muted-foreground">
                      Motif
                    </Label>
                    <p className="text-sm p-3 bg-muted/50 rounded-lg">
                      {message.motif}
                    </p>
                  </div>
                )}
                {message.justification && (
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-muted-foreground">
                      Justification
                    </Label>
                    <p className="text-sm p-3 bg-muted/50 rounded-lg">
                      {message.justification}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Message principal */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-muted-foreground">
                Message
              </Label>
              <div className="p-4 bg-background border rounded-lg whitespace-pre-wrap">
                {message.message}
              </div>
            </div>

            {/* Fichier joint */}
            {message.has_attachment && message.file_name && (
              <div className="space-y-2">
                <Label className="text-sm font-medium text-muted-foreground">
                  Fichier joint
                </Label>
                <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg w-fit">
                  <FileText className="h-6 w-6 text-muted-foreground" />
                  <div>
                    <div className="text-sm font-medium">
                      {message.file_name}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {message.file_size
                        ? `${(message.file_size / 1024).toFixed(1)} KB`
                        : "Fichier joint"}
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDownloadFile}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Télécharger
                  </Button>
                </div>
              </div>
            )}

            {/* Réponse existante */}
            {message.reponse && (
              <>
                <Separator />
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Label className="text-sm font-medium text-muted-foreground">
                      Réponse
                    </Label>
                    {message.traiteur && (
                      <div className="text-xs text-muted-foreground">
                        par {message.traiteur.prenom} {message.traiteur.nom}
                      </div>
                    )}
                    {message.date_traitement && (
                      <div className="text-xs text-muted-foreground">
                        •{" "}
                        {format(
                          new Date(message.date_traitement),
                          "dd/MM/yyyy à HH:mm",
                          { locale: fr }
                        )}
                      </div>
                    )}
                  </div>
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg whitespace-pre-wrap">
                    {message.reponse}
                  </div>
                </div>
              </>
            )}

            {/* Formulaire de réponse */}
            {canReply() && !message.reponse && isRecipient() && (
              <>
                <Separator />
                <div className="space-y-4">
                  {!showReplyForm ? (
                    <Button onClick={() => setShowReplyForm(true)}>
                      <Reply className="h-4 w-4 mr-2" />
                      Répondre
                    </Button>
                  ) : (
                    <div className="space-y-4">
                      <Label htmlFor="reply">Votre réponse</Label>
                      <Textarea
                        id="reply"
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        placeholder="Rédigez votre réponse..."
                        rows={4}
                      />
                      <div className="flex gap-2">
                        <Button
                          onClick={handleReply}
                          disabled={
                            !replyText.trim() || replyMutation.isLoading
                          }
                        >
                          {replyMutation.isLoading ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                              Envoi...
                            </>
                          ) : (
                            <>
                              <Send className="h-4 w-4 mr-2" />
                              Envoyer la réponse
                            </>
                          )}
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => {
                            setShowReplyForm(false);
                            setReplyText("");
                          }}
                        >
                          Annuler
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default MessageDetail;
